import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { SimpleFieldset } from "../../components/simplePage/simpleFieldset.tsx";
import { servicePost, buildAuthHeaders } from "../../api/client.ts";
import { batchedServicePost } from "../../api/readBatch.ts";
import { uploadFile, fetchUserStorePreview } from "../../api/fileUpload.ts";
import { buildProductImageUrl, buildProductImageUrlFallback } from "../../util/images.ts";
import { getDbFeatureFlags } from "../../util/dbFeatureFlags.ts";
import { useSession } from "../../hooks/useSession.ts";
import { ModuleId } from "../../types/session.ts";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "../../components/ui/modal.tsx";
import { ProductLockModal } from "./productLockModal.tsx";
import { LocalizedModal, loadLanguages } from "./localizedModal.tsx";
import { ExternalInfoModal } from "./externalInfoModal.tsx";
import { ProductRelationsSection, type AccessProductRelation } from "./productRelationsSection.tsx";
import { ProductPriceEntry } from "./productPriceEntry.tsx";
import { ChooseProductImageModal } from "./chooseProductImageModal.tsx";
import { applyProductImage, type ProductImageOption } from "../../api/productImages.ts";
import { getAccessCategories, type AccessCategory } from "../../api/category.ts";
import { useAlertDialog } from "../../hooks/useAlertDialog.tsx";
import { StatusIcon } from "../../components/ui/statusIcon.tsx";
import { FwRichTextarea } from "../../components/ui/fwRichTextarea.tsx";
import { FileInputSafe } from "../../components/ui/fileInputSafe.tsx";
import { OperatorSinglePicker } from "../../components/ui/operatorSinglePicker.tsx";
import { useAuth } from "../../auth/authContext.tsx";
import styles from "../../styles/formPage.module.scss";
import type { ProductEditorHandle, ProductEditorProps } from "./editProductShared.ts";

/* ── Types ──────────────────────────────────────────────── */

/** Reserve extra-info stored inside ExtraInfo JSON. */
interface ReserveExtraInfo {
  RentalPeriodMinutes?: number;
  MaxRentalPeriods?: number;
  IncludedItemInfo?: IncludedItemEntry[];
}

interface IncludedItemEntry {
  Quantity?: number;
  Products?: number[];
  Lists?: number[];
}

interface ProductExtraInfo {
  Reserve?: ReserveExtraInfo;
  [key: string]: unknown;
}

interface AccessProduct {
  ProductID: number;
  OperatorID: number;
  Name: string;
  PluralName: string;
  ShortName: string;
  Brand: string;
  ArticleNumber: string;
  Description: string;
  ProductType: number;
  BarcodeNr: string;
  PermissionID: string;
  PermissionName: string;
  BuyPrice: number;
  BuyTax: number;
  ExternalCost: number;
  RentalPrice: number;
  RentalTax: number;
  EatHerePrice: number;
  EatHereTax: number;
  ReservePeriodPrice: number | null;
  ReserveLatePeriodPrice: number | null;
  ReserveLateTaxrate: number | null;
  UnitsPerPackage: number;
  OrderMultiplier: number;
  InsaneAmount: number;
  AgeRestriction: number;
  WeightPercentAllowance: number | null;
  PriceType: number;
  ExternalItemID: string;
  ProductDepartment: string;
  ProductClass: string;
  ProductSubClass: string;
  ExternalDescription: string;
  ExternalName: string;
  ExternalArticleNr: string;
  ForceScan: boolean;
  IsExpirable: boolean;
  Deleted: boolean;
  VideoUrl: string;
  ImageUrl: string;
  AdditionalImages: string;
  Localization: string;
  ExternalInfo: string;
  ExtraInfo: string;
  LockHours: string;
  CategoryIDs: string;
  StoreLocationIDs: string;
  SlotColor: string;
  PdfFiles: string;
  ReserveInfo: string;
  IncludedItems: string;
}

/**
 * GetAccessProduct / SaveAccessProduct return the backend IDS.AccessProduct, whose
 * price columns (RentalPrice/RentalTax/EatHerePrice/EatHereTax and the Reserve*
 * fields) are never SELECTed, so they come back as 0/null; buy price/tax are not on
 * that class at all. The authoritative prices live in editor state (loaded from the
 * separate Price dict via GetAccessProductWithPrice). Strip every price field from
 * the backend object before merging it into state — otherwise the spread blanks the
 * prices and a later save persists 0 (e.g. after adding an image then re-saving).
 */
function withoutBackendPrices(p: AccessProduct): Partial<AccessProduct> {
  const {
    BuyPrice: _bp, BuyTax: _bt, RentalPrice: _rp, RentalTax: _rt,
    EatHerePrice: _ep, EatHereTax: _et,
    ReservePeriodPrice: _rpp, ReserveLatePeriodPrice: _rlp, ReserveLateTaxrate: _rlt,
    ...rest
  } = p;
  return rest;
}

/**
 * What the backend actually sends. `IDS.AccessProduct` names the column
 * `AdditionalImageUrls`; this form calls the same field `AdditionalImages` and
 * `handleSave` renames it back on the way out.
 *
 * Nothing renamed it on the way IN, so the value arrived under a key the form never
 * reads, and `AdditionalImages` stayed `undefined` for the whole session. Three
 * consequences, all silent: the additional-image list rendered empty, the merge in
 * `confirmPendingImage` had nothing to merge with and dropped previously committed
 * names, and every ordinary product save posted `AdditionalImageUrls: ""` — blanking
 * the column for a product nobody had touched the images of.
 *
 * Declared as a distinct type so the compiler, not a reviewer, is what stops it
 * happening again: a backend response can only reach editor state through
 * {@link fromWire}.
 */
type AccessProductWire = Omit<AccessProduct, "AdditionalImages"> & { AdditionalImageUrls: string };

function fromWire(p: AccessProductWire): AccessProduct {
  const { AdditionalImageUrls, ...rest } = p;
  return { ...rest, AdditionalImages: AdditionalImageUrls ?? "" };
}

interface AccessProductContainer {
  Item: AccessProductWire;
  Categories: { CategoryID: number; CategoryName?: string; OperatorID?: number }[];
  Price: Record<number, number>;
}

type Props = ProductEditorProps & { moduleId: number };

/* ── Product Types per module ─────────────────────────── */

const ACCESS_PRODUCT_TYPES = [
  { value: 1, label: "Normal" },
  { value: 6 + (1 << 16), label: "Weight, Gram" },
  { value: 6 + (3 << 16), label: "Weight, Hectogram" },
  { value: 6 + (4 << 16), label: "Weight, Kilogram" },
  { value: 6 + (23 << 16), label: "Weight, Ounce" },
  { value: 6 + (25 << 16), label: "Weight, Pound" },
  { value: 8, label: "Vending" },
  { value: 9, label: "AlarmAbove" },
  { value: 10, label: "AlarmBelow" },
  { value: 11, label: "Accessory" },
  { value: 12, label: "Dispense" },
  { value: 13, label: "ExternalPickup" },
];

const AIM_PRODUCT_TYPES = [
  { value: 1, label: "Percentage" },
  { value: 2, label: "Parts" },
  { value: 3, label: "Parts (no decimals)" },
  { value: 4, label: "Percentage without package" },
  { value: 5, label: "Package" },
  { value: 6, label: "ManualAddProduct" },
  { value: 6 + (1 << 16), label: "Weight, Gram" },
  { value: 6 + (3 << 16), label: "Weight, Hectogram" },
  { value: 6 + (4 << 16), label: "Weight, Kilogram" },
  { value: 6 + (23 << 16), label: "Weight, Ounce" },
  { value: 6 + (25 << 16), label: "Weight, Pound" },
  { value: 9, label: "AlarmAbove" },
  { value: 10, label: "AlarmBelow" },
  { value: 11, label: "Accessory" },
];

const DEFAULT_PRODUCT_TYPES = [
  { value: 1, label: "Normal" },
  { value: 2, label: "Similar Cabinet" },
  { value: 3, label: "Similar Store" },
  { value: 4, label: "Attachment Cabinet" },
  { value: 5, label: "Attachment Store" },
];

/** Convert raw ProductType (0-based) to the combo value matching WebAdmin2 encoding. */
function productTypeToComboValue(raw: number, moduleId: number): number {
  if (raw === undefined || raw < 0) return 1;
  // Access: bare 6 → 6+(4<<16) (Weight, Kilogram default)
  if (moduleId === ModuleId.Access && raw + 1 === 6) return 6 + (4 << 16);
  return raw === 0 ? 1 : raw + 1;
}

/** Convert combo value back to raw ProductType for saving. */
function comboValueToProductType(comboVal: number): number {
  return comboVal <= 1 ? 0 : comboVal - 1;
}

/** Image upload limits (matching backend constants). */
const IMAGE_MAX_SIZE_MB = 2.44;
const IMAGE_MAX_RESOLUTION_MP = 2.1;

/* ── Component ──────────────────────────────────────────── */

export const EditProductAccess = forwardRef<ProductEditorHandle, Props>(
  function EditProductAccess({ moduleId, productId, prefill, onSaved, onStateChange, onAuxiliaryChange }, ref) {
  const { session, cache } = useSession();
  const { upsertProduct } = useAuth();
  const { confirm } = useAlertDialog();
  const [product, setProduct] = useState<AccessProduct>(emptyProduct());
  const [productTypeCombo, setProductTypeCombo] = useState(1);
  // Prices keyed by PriceType (1..9) — the server Price dict is already PT-keyed.
  // Single source of truth the ProductPriceEntry panel edits.
  const [priceRec, setPriceRec] = useState<Record<number, number | null>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  // True once the user edits any field — drives the modal's discard-on-close
  // confirm. Reset on (re)load and after a successful save.
  const [dirty, setDirty] = useState(false);
  /** Field keys pre-applied by a caller (Issa's editProduct action) and not yet saved,
   *  highlighted in the form so the operator sees exactly what is being proposed. */
  const [changedFields, setChangedFields] = useState<string[]>([]);

  // Bubble saving + status + dirty so the outer Modal footer can show Saving
  // state and guard close. Remap the internal `{type, text}` to the shared shape.
  useEffect(() => {
    onStateChange?.({ saving, status: message, dirty });
  }, [saving, message, dirty, onStateChange]);
  const [barcodeInput, setBarcodeInput] = useState("");
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [uploading, setUploading] = useState<string | null>(null);
  const [lockHoursOpen, setLockHoursOpen] = useState(false);
  const [localizedOpen, setLocalizedOpen] = useState(false);
  const [languages, setLanguages] = useState<{ id: string; label: string }[]>([]);
  const [selectedLanguage, setSelectedLanguage] = useState("");
  const [tempRelations, setTempRelations] = useState<AccessProductRelation[]>([]);
  const [availableCategories, setAvailableCategories] = useState<AccessCategory[]>([]);
  const [assignedCategories, setAssignedCategories] = useState<{ CategoryID: number; CategoryName: string }[]>([]);
  const [categoryComboValue, setCategoryComboValue] = useState("");
  const [externalInfoOpen, setExternalInfoOpen] = useState(false);
  const [reserveExtra, setReserveExtra] = useState<ReserveExtraInfo>({});
  const [showOnlyRequired, setShowOnlyRequired] = useState(false);
  const [postCreateImageOpen, setPostCreateImageOpen] = useState(false);
  const postCreateImageRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLDivElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const additionalImageInputRef = useRef<HTMLInputElement>(null);
  const [pendingImage, setPendingImage] = useState<{
    kind: "main" | "additional";
    storedPath: string;
    imageName: string;
    previewUrl: string;
  } | null>(null);
  const [confirming, setConfirming] = useState(false);
  // "Choose existing image" picker. On a new product the pick is held here and written right
  // after the create succeeds (handleSave), since WebSaveProductImageNameAIM needs a product id.
  const [chooseImageOpen, setChooseImageOpen] = useState(false);
  const [pendingExistingImage, setPendingExistingImage] = useState<ProductImageOption | null>(null);

  const isNew = !productId;
  const isAccess = moduleId === ModuleId.Access;
  const isAIM = moduleId === ModuleId.AIM;
  const isAccessOrAIM = isAccess || isAIM;

  // Load language options once
  useEffect(() => {
    loadLanguages().then(setLanguages);
  }, []);

  // Load available categories once
  useEffect(() => {
    getAccessCategories().then(res => {
      if (!res.bError && res.oData) setAvailableCategories(res.oData);
    });
  }, []);

  // Revoke any pending preview object URL on unmount to avoid leaks.
  useEffect(() => {
    return () => {
      if (pendingImage) URL.revokeObjectURL(pendingImage.previewUrl);
    };
  }, [pendingImage]);

  const operatorId = useMemo(() => cache?.OwnerOperatorID ?? product.OperatorID ?? 0, [cache, product.OperatorID]);
  const showOperatorSelect = !cache?.OwnerOperatorID;
  // Image folder the product's main image is read from: uploads commit to
  // pictures/f/{product.OperatorID || 0}. A new product with no operator picked yet is
  // expected to land on the caller's own operator, so list that folder; handleSave checks
  // the operator the create really stamped before applying a pre-create pick.
  // Whether the product exists yet is product.ProductID.
  const productCreated = product.ProductID > 0;
  const imageFolderOperatorId = !productCreated
    ? (product.OperatorID || cache?.OwnerOperatorID || 0)
    : (product.OperatorID || 0);

  // Ingress groups that have Type="Product" — for permission assignment
  const productIngressGroups = useMemo(() => {
    const groups = (cache?.CommonData?.IngressGroup ?? []) as { ID: number; Name: string; Types?: { Type: string; Items?: number[] }[] }[];
    return groups.filter(g => g.Types?.some(t => t.Type === "Product"));
  }, [cache]);

  // Per-database feature flags (see getDbFeatureFlags), replacing the old
  // hard-coded customer-id / endpoint-id lists.
  const flags = useMemo(() => getDbFeatureFlags(cache), [cache]);

  const hasRequiredFields = !!flags.ProductRequiredFields;
  const showAll = !hasRequiredFields || !showOnlyRequired;

  /** Red asterisk for required fields */
  const req = (label: string, always = false) =>
    (always || hasRequiredFields) ? <>{label} <span style={{ color: "var(--color-error)" }}>*</span></> : label;

  // Visibility flags (was WebAdmin2 HideFields() customer lists → DbFeatureFlags).
  const isNormalType = productTypeCombo === 1;
  const showRental = useMemo(() => {
    if (isAIM) return false;
    if (isAccess) return !!flags.ProductShowRental && isNormalType;
    if (moduleId === ModuleId.Rent || moduleId === ModuleId.NeoCaddie) return true;
    return false;
  }, [moduleId, isAccess, isAIM, flags, isNormalType]);

  const showReserve = useMemo(
    () => isAccess && !!flags.ProductShowReserve && isNormalType,
    [isAccess, flags, isNormalType],
  );

  const showEatHere = useMemo(
    () => !isAIM && isAccess && !!flags.ProductShowEatHere,
    [isAccess, isAIM, flags],
  );

  const hideReserveLateTaxrate = !!flags.ProductHideReserveLateTaxrate;

  // Module-specific product type options
  const productTypes = useMemo(() => {
    if (isAccess) return ACCESS_PRODUCT_TYPES;
    if (isAIM) return AIM_PRODUCT_TYPES;
    return DEFAULT_PRODUCT_TYPES;
  }, [isAccess, isAIM]);

  // "Other" modules hide many things
  const isOtherModule = !isAccessOrAIM;

  // ── Changed-field highlighting (pre-applied, unsaved edits) ─────────
  const changed = useMemo(() => new Set(changedFields), [changedFields]);
  /** Row class — adds the "changed" accent when any of the given fields was pre-applied. */
  const rowClass = useCallback(
    (...fields: string[]) =>
      fields.some((f) => changed.has(f))
        ? `${styles.fieldRowStacked} ${styles.fieldChanged}`
        : styles.fieldRowStacked,
    [changed],
  );
  /** Section title marker, so a change inside a collapsed section is still visible. */
  const sectionTitle = useCallback(
    (title: string, ...fields: string[]) =>
      fields.some((f) => changed.has(f)) ? (
        <>{title}<span className={styles.tabChangedDot} aria-label="changed" /></>
      ) : (
        title
      ),
    [changed],
  );

  useEffect(() => {
    if (productId <= 0) {
      const empty = emptyProduct();
      // AIM defaults: price=2, tax=1
      if (isAIM) { empty.BuyPrice = 2; empty.BuyTax = 1; }
      setProduct(empty);
      setProductTypeCombo(1);
      setPriceRec(isAIM ? { 1: 2, 2: 1 } : {});
      setAssignedCategories([]);
      setDirty(false);
      return;
    }
    setLoading(true);
    batchedServicePost<AccessProductContainer>("GetAccessProductWithPrice", { id: productId })
      .then(result => {
        if (!result.bError && result.oData) {
          const { Item, Categories: cats, Price } = result.oData;
          const mapped: AccessProduct = {
            ...fromWire(Item),
            BuyPrice: Price?.[1] ?? 0,
            BuyTax: Price?.[2] ?? 0,
            RentalPrice: Price?.[3] ?? 0,
            RentalTax: Price?.[4] ?? 0,
            EatHerePrice: Price?.[5] ?? 0,
            EatHereTax: Price?.[6] ?? 0,
            ReserveLatePeriodPrice: Price?.[7] ?? null,
            ReserveLateTaxrate: Price?.[8] ?? null,
            ReservePeriodPrice: Price?.[9] ?? null,
          };
          if (cats?.length) {
            mapped.CategoryIDs = cats.map(c => c.CategoryID).join(",");
          }
          const loadedPrices: Record<number, number | null> = {
            1: Price?.[1] ?? null, 2: Price?.[2] ?? null,
            3: Price?.[3] ?? null, 4: Price?.[4] ?? null,
            5: Price?.[5] ?? null, 6: Price?.[6] ?? null,
            7: Price?.[7] ?? null, 8: Price?.[8] ?? null, 9: Price?.[9] ?? null,
          };
          // A caller may pre-apply values for review (Issa's editProduct action). Layer
          // them over the stored ones and leave the form dirty so Save is the next step.
          // Field keys, so the form can highlight exactly what changed.
          const applied: string[] = [];
          if (prefill?.name != null && prefill.name !== mapped.Name) {
            mapped.Name = prefill.name;
            applied.push("Name");
          }
          if (prefill?.externalItemId != null && prefill.externalItemId !== mapped.ExternalItemID) {
            mapped.ExternalItemID = prefill.externalItemId;
            applied.push("ExternalItemID");
          }
          if (prefill?.externalCost != null && prefill.externalCost !== mapped.ExternalCost) {
            mapped.ExternalCost = prefill.externalCost;
            applied.push("ExternalCost");
          }
          for (const [pt, v] of Object.entries(prefill?.prices ?? {})) {
            if (v == null) continue;
            const key = Number(pt);
            if (loadedPrices[key] === v) continue;
            loadedPrices[key] = v;
            applied.push("Price");
          }
          setProduct(mapped);
          setPriceRec(loadedPrices);
          setDirty(applied.length > 0);
          setChangedFields(applied);
          setProductTypeCombo(productTypeToComboValue(mapped.ProductType, moduleId));
        } else {
          setMessage({ type: "error", text: "Failed to load product" });
        }
      })
      .finally(() => setLoading(false));
  }, [productId, moduleId]);

  // Sync assigned categories when product or available categories change
  useEffect(() => {
    if (!product.CategoryIDs || availableCategories.length === 0) {
      if (!product.CategoryIDs) setAssignedCategories([]);
      return;
    }
    const ids = product.CategoryIDs.split(",").map(Number).filter(n => n > 0);
    const assigned = ids.map(id => {
      const cat = availableCategories.find(c => c.CategoryID === id);
      return { CategoryID: id, CategoryName: cat ? `${cat.CategoryName} (OperatorID: ${cat.OperatorID ?? 0})` : `Unknown (${id})` };
    });
    setAssignedCategories(assigned);
  }, [product.CategoryIDs, availableCategories]);

  // Parse reserve extra info from ExtraInfo JSON
  useEffect(() => {
    if (!product.ExtraInfo) { setReserveExtra({}); return; }
    try {
      const parsed: ProductExtraInfo = JSON.parse(product.ExtraInfo);
      setReserveExtra(parsed.Reserve ?? {});
    } catch { setReserveExtra({}); }
  }, [product.ExtraInfo]);

  const updateReserveExtra = useCallback((field: keyof ReserveExtraInfo, value: number | undefined) => {
    setReserveExtra(prev => {
      const next = { ...prev, [field]: value };
      // Merge back into ExtraInfo JSON
      let extra: ProductExtraInfo = {};
      try { if (product.ExtraInfo) extra = JSON.parse(product.ExtraInfo); } catch { /* ignore */ }
      extra.Reserve = next;
      setProduct(p => ({ ...p, ExtraInfo: JSON.stringify(extra) }));
      return next;
    });
    setDirty(true);
  }, [product.ExtraInfo]);

  const handleChange = useCallback((field: keyof AccessProduct, value: string | number | boolean | null) => {
    setProduct(prev => ({ ...prev, [field]: value }));
    setDirty(true);
  }, []);

  const toggle = useCallback((section: string) => {
    setCollapsed(prev => ({ ...prev, [section]: !prev[section] }));
  }, []);

  const handleSave = useCallback(async () => {
    if (!product.Name.trim()) {
      setMessage({ type: "error", text: "Product name is required." });
      return;
    }
    // BuyPrice/BuyTax come from the Price dictionary, not the backend AccessProduct
    // class. Resolve them up front (AIM auto-defaults to 2/1) so we can warn
    // before saving — done before setSaving so the confirm isn't shown mid-spinner.
    let buyPrice = priceRec[1] ?? 0;
    let buyTax = priceRec[2] ?? 0;
    if (isAIM) {
      if (!buyPrice) buyPrice = 2;
      if (!buyTax) buyTax = 1;
    }
    if (buyTax < 0) {
      setMessage({ type: "error", text: "Tax cannot be negative." });
      return;
    }
    // Warn before saving/creating a product with no price (price 0). AIM is
    // exempt — it auto-defaults a price above, so buyPrice is never 0 here.
    let canSaveEmptyPrice = false;
    if (buyPrice <= 0) {
      if (!isAIM) {
        const ok = await confirm({
          title: "Save without a price?",
          body: `${product.Name.trim() || "This product"} has no price set (price is 0). Save it anyway?`,
          confirmLabel: "Save without price",
        });
        if (!ok) return;
      }
      canSaveEmptyPrice = true;
    }

    setSaving(true);
    setMessage(null);
    try {
      // Include temp relations for new products (matching WebAdmin2 SaveAccessProduct signature)
      const listRelations = isNew ? tempRelations.map(r => ({
        RelationID: r.RelationID,
        ProductID: 0,
        RelationType: r.RelationType,
        Deleted: false,
        ID: 1,
      })) : [];
      // Build product matching backend IDS.AccessProduct class:
      // - Strip frontend-only fields (BuyPrice, BuyTax, PermissionID, etc.)
      // - Remap mismatched names (AdditionalImages → AdditionalImageUrls, StoreLocationIDs → StoreLocation)
      const { PermissionID, PermissionName, BuyPrice: _bp, BuyTax: _bt,
              Localization, ExternalInfo, LockHours, CategoryIDs, StoreLocationIDs,
              AdditionalImages, PdfFiles, ReserveInfo, IncludedItems,
              ...backendProduct } = product;
      const saveProduct = {
        ...backendProduct,
        ProductType: comboValueToProductType(productTypeCombo),
        AdditionalImageUrls: AdditionalImages ?? "",
        StoreLocation: StoreLocationIDs ?? "",
        OperatorID: product.OperatorID || null,
        RentalPrice: priceRec[3] ?? 0,
        RentalTax: priceRec[4] ?? 0,
        EatHerePrice: priceRec[5] ?? 0,
        EatHereTax: priceRec[6] ?? 0,
        ReserveLatePeriodPrice: priceRec[7] ?? null,
        ReserveLateTaxrate: priceRec[8] ?? null,
        ReservePeriodPrice: priceRec[9] ?? null,
      };
      const result = await servicePost<AccessProductWire>("SaveAccessProduct", {
        product: saveProduct,
        price: String(buyPrice || 0),
        tax: String(buyTax || 0),
        canSaveEmptyPrice,
        categories: assignedCategories,
        relations: listRelations,
      });
      if (result.bError || !result.oData) {
        setMessage({ type: "error", text: result.sMessage ?? "Failed to save product" });
      } else {
        const wasNew = isNew;
        // result.oData is IDS.AccessProduct from GetAccessProductInternal — its price
        // columns come back as 0/null (price lives in S_Generic_Price / the separate
        // Price dict). Strip them before merging so the spread can't blank rental/
        // eat-here/reserve prices; then restore the buy price/tax just saved (which
        // may differ from prev, e.g. AIM auto-defaults). A subsequent save in the same
        // session then resubmits the real prices instead of 0.
        setProduct(prev => ({ ...prev, ...withoutBackendPrices(fromWire(result.oData!)), BuyPrice: buyPrice, BuyTax: buyTax }));
        setPriceRec((prev) => ({ ...prev, 1: buyPrice, 2: buyTax }));
        setDirty(false);
        // Pre-applied suggestions are stored now — drop the "pending change" highlights.
        setChangedFields([]);
        setProductTypeCombo(productTypeToComboValue(result.oData.ProductType, moduleId));
        upsertProduct({
          ProductID: result.oData.ProductID,
          Name: result.oData.Name,
          Deleted: result.oData.Deleted,
        });
        let doneMessage: { type: "success" | "error"; text: string } =
          { type: "success", text: wasNew ? "Product Created" : "Product Updated" };
        const pick = pendingExistingImage;
        if (pick) {
          // An existing image chosen before the product existed: apply it now. A shared image
          // (folder 0) works for any product — the server copies it into the product's operator
          // folder. An operator-folder image only fits a product the create stamped with that
          // same operator; otherwise leave the product without an image and say so rather than
          // point it at a file it cannot load.
          setPendingExistingImage(null);
          const createdOperator = result.oData.OperatorID || 0;
          if (pick.OperatorID !== 0 && createdOperator !== pick.OperatorID) {
            doneMessage = { type: "error", text: `Product Created, but the chosen image ${pick.ImageName} was not applied: it is in another operator's image folder. Choose or upload the image again.` };
          } else {
            try {
              const applied = await applyProductImage(result.oData.ProductID, pick);
              if (applied.bError || !applied.oData) {
                doneMessage = { type: "error", text: `Product Created, but the image could not be set: ${applied.sMessage ?? "unknown error"}` };
              } else {
                const storedName = applied.oData;
                setProduct(prev => ({ ...prev, ImageUrl: storedName }));
                doneMessage = { type: "success", text: `Product Created with image ${storedName}` };
              }
            } catch (err: unknown) {
              doneMessage = { type: "error", text: `Product Created, but the image could not be set: ${err instanceof Error ? err.message : "unknown error"}` };
            }
          }
        }
        setMessage(doneMessage);
        formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        if (wasNew && !(pick && doneMessage.type === "success")) setPostCreateImageOpen(true);
        onSaved();
      }
    } finally {
      setSaving(false);
    }
  }, [product, priceRec, productTypeCombo, isNew, isAIM, moduleId, tempRelations, assignedCategories, onSaved, upsertProduct, confirm, pendingExistingImage]);

  /** Picker result. Existing product: written at once, like a confirmed upload. New product:
   *  held and written after the create (see handleSave). Returns an error text or null. */
  const handleChooseExistingImage = useCallback(async (option: ProductImageOption): Promise<string | null> => {
    if (!productCreated) {
      setPendingExistingImage(option);
      setDirty(true);
      setChooseImageOpen(false);
      return null;
    }
    // A shared image is copied into the product's operator folder server-side; the stored name
    // can differ from the listed one when that folder already had another file of that name.
    const applied = await applyProductImage(product.ProductID, option);
    if (applied.bError || !applied.oData) return applied.sMessage ?? "Failed to set the product image";
    const storedName = applied.oData;
    // Same reason as confirmPendingImage: the column now holds this name, so the form must too,
    // or the next ordinary save writes the old name back.
    setProduct(prev => ({ ...prev, ImageUrl: storedName }));
    setMessage({ type: "success", text: `Product image set to ${storedName}` });
    setChooseImageOpen(false);
    return null;
  }, [productCreated, product.ProductID]);

  useImperativeHandle(ref, () => ({ save: handleSave }), [handleSave]);

  /* ── Image Uploads ── */

  const validateImageClient = useCallback((file: File): string | null => {
    const sizeMB = file.size / (1024 * 1024);
    if (sizeMB > IMAGE_MAX_SIZE_MB) return `File larger than the limit of ${IMAGE_MAX_SIZE_MB} MB (${sizeMB.toFixed(2)} MB)`;
    return null;
  }, []);

  const handleUploadProductImage = useCallback(async (file: File) => {
    if (!session) return;
    const clientError = validateImageClient(file);
    if (clientError) { setMessage({ type: "error", text: clientError }); return; }

    setUploading("image");
    setMessage(null);
    try {
      const authHeaders = await buildAuthHeaders();
      // Upload: server validates (size/MP/format) and stores in UserStore.
      // No commit to pictures/f yet — waits for user confirmation below.
      const uploaded = await uploadFile(file, authHeaders, {
        purpose: "product-image",
        context: { imageName: file.name },
      });
      if (uploaded.ProcessingError) {
        setMessage({ type: "error", text: uploaded.ProcessingError });
        return;
      }
      const previewUrl = await fetchUserStorePreview(uploaded.StoredPath, authHeaders);
      setPendingImage({ kind: "main", storedPath: uploaded.StoredPath, imageName: file.name, previewUrl });
    } catch (err: unknown) {
      setMessage({ type: "error", text: err instanceof Error ? err.message : "Upload failed" });
    } finally {
      setUploading(null);
    }
  }, [session, validateImageClient]);

  const cancelPendingImage = useCallback(async () => {
    if (!pendingImage) return;
    const { previewUrl, storedPath } = pendingImage;
    URL.revokeObjectURL(previewUrl);
    setPendingImage(null);
    // Best-effort delete of the UserStore temp file. Ignored on failure —
    // CleanupService will age it out regardless.
    try {
      const slash = storedPath.indexOf("/");
      if (slash > 0) {
        const month = storedPath.slice(0, slash);
        const name = storedPath.slice(slash + 1);
        await servicePost<boolean>("WebDeleteOwnUpload", { month, name });
      }
    } catch {
      // no-op — server-side cleanup fallback
    }
  }, [pendingImage]);

  const confirmPendingImage = useCallback(async () => {
    if (!session || !pendingImage) return;
    setConfirming(true);
    setMessage(null);
    try {
      const method = pendingImage.kind === "main" ? "WebUploadProductImage" : "WebUploadAdditionalProductImageNew";
      const args = pendingImage.kind === "main"
        ? {
            tempFilename: pendingImage.storedPath,
            imageName: pendingImage.imageName,
            ignoreVerification: false,
            operatorId: product.OperatorID || 0,
          }
        : {
            productId: product.ProductID,
            tempFilenames: [pendingImage.storedPath],
            ignoreVerification: false,
            operatorId: product.OperatorID || 0,
          };
      const result = await servicePost<string | string[]>(method, args);
      if (result.bError || result.oData == null) {
        setMessage({ type: "error", text: result.sMessage ?? "Image save failed" });
        return;
      }
      // The upload RPCs only commit the file to storage — the
      // Access_Product.ImageUrl / AdditionalImageUrls column is NOT updated.
      // Server returns the sanitised filename(s); pair the upload with the
      // corresponding DB writer so the saved product actually references the
      // new file. Mirrors WebAdmin2's two-step pattern (Access.ts:2555-2584).
      if (pendingImage.kind === "main") {
        const safeName = result.oData as string;
        const setDb = await servicePost<boolean>("WebSaveProductImageNameAIM",
          { name: safeName, productID: product.ProductID });
        if (setDb.bError || !setDb.oData) {
          setMessage({ type: "error", text: setDb.sMessage ?? "Failed to record new image name" });
          return;
        }
        // The committed name is authoritative from here, so put it in the form before
        // anything else can fail. The form otherwise still holds the previous name, and
        // the next ordinary product save writes that back over the image just committed —
        // the image reverts and nothing reports why. `safeName` is what the server stored:
        // WebUploadProductImage returns SafeProductImageFilename(imageName), and
        // WebSaveProductImageNameAIM applies the same (idempotent) sanitiser to it.
        setProduct(prev => ({ ...prev, ImageUrl: safeName }));
      } else {
        // Append the newly committed name to AdditionalImageUrls.
        // WebSaveProductAdditionalImageName overwrites the whole column, so
        // merge with the existing value before writing.
        const safeNames = (result.oData as string[]).filter(n => !!n);
        if (safeNames.length > 0) {
          // Backend tokenizes AdditionalImageUrls on "," (or "|") — see
          // TokenizeAdditionalImageEntries in Products.Core.vb. Use "," to match.
          const existing = (product.AdditionalImages ?? "").trim();
          const merged = existing
            ? `${existing},${safeNames.join(",")}`
            : safeNames.join(",");
          const setDb = await servicePost<boolean>("WebSaveProductAdditionalImageName",
            { name: merged, productID: product.ProductID });
          if (setDb.bError || !setDb.oData) {
            setMessage({ type: "error", text: setDb.sMessage ?? "Failed to record new image name" });
            return;
          }
          // Same reason as the main image above: the column now holds `merged`, and a form
          // still holding the old list would post it back on the next save.
          setProduct(prev => ({ ...prev, AdditionalImages: merged }));
        }
      }
      setMessage({
        type: "success",
        text: pendingImage.kind === "main" ? "Product image saved" : "Additional image saved",
      });
      URL.revokeObjectURL(pendingImage.previewUrl);
      setPendingImage(null);
      // Picks up anything the server normalised (FixAdditionalProductImages rewrites the
      // list). Best-effort on purpose — the names above are already in the form, so a
      // refetch that fails costs nothing. It used to be the ONLY write-back, and it failed
      // on every call: GetAccessProduct declared a required `operatorId` it never read, so
      // dispatch answered InvalidParameters and the guard below swallowed it in silence.
      // That parameter is gone; this keeps working either way.
      const refresh = await servicePost<AccessProductWire>("GetAccessProduct", { id: product.ProductID });
      // GetAccessProduct returns price columns as 0/null — strip them so the merge
      // keeps every in-state price (buy/rental/eat-here/reserve). Without this the
      // image step blanks the non-buy prices and the next save persists 0.
      if (!refresh.bError && refresh.oData) setProduct(prev => ({ ...prev, ...withoutBackendPrices(fromWire(refresh.oData!)) }));
    } catch (err: unknown) {
      setMessage({ type: "error", text: err instanceof Error ? err.message : "Save failed" });
    } finally {
      setConfirming(false);
    }
  }, [session, pendingImage, product.ProductID, product.OperatorID]);

  const handleUploadAdditionalImage = useCallback(async (file: File) => {
    if (!session) return;
    const clientError = validateImageClient(file);
    if (clientError) { setMessage({ type: "error", text: clientError }); return; }

    setUploading("additional");
    setMessage(null);
    try {
      const authHeaders = await buildAuthHeaders();
      const uploaded = await uploadFile(file, authHeaders, {
        purpose: "product-image",
        context: { imageName: file.name },
      });
      if (uploaded.ProcessingError) {
        setMessage({ type: "error", text: uploaded.ProcessingError });
        return;
      }
      const previewUrl = await fetchUserStorePreview(uploaded.StoredPath, authHeaders);
      setPendingImage({ kind: "additional", storedPath: uploaded.StoredPath, imageName: file.name, previewUrl });
    } catch (err: unknown) {
      setMessage({ type: "error", text: err instanceof Error ? err.message : "Upload failed" });
    } finally {
      setUploading(null);
    }
  }, [session, validateImageClient]);

  // Bubble auxiliary buttons (Edit Lock Hours, Delete/Restore Product) to the
  // outer Modal's `ModalFooter` — left-aligned per AGENTS.md § Create/Edit
  // Modal Guidelines. The modals themselves (ProductLockModal / ExternalInfoModal)
  // are rendered at the end of the body so they keep their own open-state.
  useEffect(() => {
    if (isNew) { onAuxiliaryChange?.(null); return; }
    onAuxiliaryChange?.(
      <div style={{ display: "flex", gap: "var(--space-2)", alignItems: "center" }}>
        <button
          className={styles.secondaryBtn}
          onClick={() => setLockHoursOpen(true)}
        >
          Edit Lock Hours
        </button>
        <button
          className={styles.secondaryBtn}
          style={product.Deleted
            ? undefined
            : { color: "var(--color-error)", borderColor: "var(--color-error)" }}
          onClick={async () => {
            const isDelete = !product.Deleted;
            const ok = await confirm({
              title: isDelete ? `Delete product?` : `Restore product?`,
              body: product.Name,
              confirmLabel: isDelete ? "Delete" : "Restore",
              destructive: isDelete,
            });
            if (ok) handleChange("Deleted", !product.Deleted);
          }}
        >
          {product.Deleted ? "Restore Product" : "Delete Product"}
        </button>
      </div>,
    );
  }, [isNew, product.Deleted, product.Name, handleChange, confirm, onAuxiliaryChange]);

  if (loading) return <p>Loading...</p>;

  return (
    <>
      <div ref={formRef} />
      {changed.size > 0 && (
        <div className={styles.changeNotice} role="status">
          <StatusIcon kind="info" size={16} style={{ color: "var(--color-primary)" }} />
          <span>
            Highlighted fields are the suggested changes — nothing is saved until you press Save.
          </span>
        </div>
      )}
      <ProductLockModal open={lockHoursOpen} onClose={() => setLockHoursOpen(false)} productId={product.ProductID} />
      <ExternalInfoModal open={externalInfoOpen} onClose={() => setExternalInfoOpen(false)}
        productId={product.ProductID} operatorId={operatorId} />

      {hasRequiredFields && (
        <div style={{ display: "flex", gap: "var(--space-2)", marginBottom: "var(--space-2)" }}>
          <button className={showOnlyRequired ? styles.tabActive : styles.tab} onClick={() => setShowOnlyRequired(true)}>Required Fields</button>
          <button className={!showOnlyRequired ? styles.tabActive : styles.tab} onClick={() => setShowOnlyRequired(false)}>All Fields</button>
        </div>
      )}

      {/* ── Permission ────────────────────────────── */}
      {showAll && showOperatorSelect && (
        <AccordionSection title="Permission" collapsed={collapsed["permission"]} onToggle={() => toggle("permission")}>
          {productIngressGroups.length > 1 && (
            <div className={styles.fieldRowStacked}>
              <label>Ingress Group</label>
              <select className={styles.fieldSelect} multiple
                value={product.PermissionID ? product.PermissionID.split(",") : []}
                onChange={e => {
                  const selected = Array.from(e.target.selectedOptions, o => o.value);
                  handleChange("PermissionID", selected.join(","));
                }}
                style={{ minHeight: 80 }}
              >
                {productIngressGroups.map(g => (
                  <option key={g.ID} value={String(g.ID)}>{g.Name}</option>
                ))}
              </select>
              <p className={styles.hint}>Hold Ctrl/Cmd to select multiple groups.</p>
            </div>
          )}
          <div className={styles.fieldRowStacked} style={{ maxWidth: 280 }}>
            <label>Operator</label>
            <OperatorSinglePicker
              value={product.OperatorID || 0}
              onChange={(id) => handleChange("OperatorID", id)}
            />
          </div>
          
        
        </AccordionSection>
      )}

      {/* ── Product ───────────────────────────────── */}
      <AccordionSection title={sectionTitle("Product", "Name", "ExternalItemID", "ExternalCost")} collapsed={collapsed["product"]} onToggle={() => toggle("product")}>
        <div style={{ display: "flex", gap: "var(--space-3)" }}>
          <div className={rowClass("Name")} style={{ flex: 1 }}>
            <label>{req("Product Name", true)}</label>
            <input className={styles.fieldInput} value={product.Name} onChange={e => handleChange("Name", e.target.value)} />
          </div>
          {showAll && (
            <div className={styles.fieldRowStacked} style={{ flex: 1 }}>
              <label>Short Name</label>
              <input className={styles.fieldInput} value={product.ShortName} onChange={e => handleChange("ShortName", e.target.value)} />
            </div>
          )}
          <div className={styles.fieldRowStacked} style={{ flex: 1 }}>

          </div>
        </div>
        {showAll && (
          <div style={{ display: "flex", gap: "var(--space-3)", alignItems: "flex-end" }}>
            {!isOtherModule && (
              <div className={styles.fieldRowStacked} style={{ flex: 1, maxWidth: 150 }}>
                <label>Product Type</label>
                <select className={styles.fieldSelect} value={productTypeCombo} onChange={e => { setProductTypeCombo(parseInt(e.target.value)); setDirty(true); }}>
                  {productTypes.map(pt => (
                    <option key={pt.value} value={pt.value}>{pt.label}</option>
                  ))}
                </select>
                {isAccess && <p className={styles.hint}>Normal: Shows the quantity in whole numbers.</p>}
                {isAIM && (
                  <p className={styles.hint}>
                    Percentage: shows amount in %. Parts: shows amount with decimals. Parts (no decimals): whole numbers only.
                    Percentage without package: percentage based on weight minus package. Package: added to cart if &gt;60% weight removed. ManualAddProduct: allows adding products not in slots.
                  </p>
                )}
              </div>
            )}
          </div>
        )}
        {showAll && isAIM && (
          <div style={{ display: "flex", gap: "var(--space-3)" }}>
            <div className={styles.fieldRowStacked} style={{ flex: 1, maxWidth: 180 }}>
              <label>Units per Package</label>
              <input className={styles.fieldInput} type="number" min="1" step="1" value={product.UnitsPerPackage}
                onChange={e => handleChange("UnitsPerPackage", parseInt(e.target.value) || 1)} />
            </div>
            <div className={styles.fieldRowStacked} style={{ flex: 1, maxWidth: 180 }}>
              <label>Order Multiplier</label>
              <input className={styles.fieldInput} type="number" step="1" value={product.OrderMultiplier}
                onChange={e => handleChange("OrderMultiplier", parseInt(e.target.value) || 1)} />
            </div>
          </div>
        )}
        {/* AIM keeps the safety data sheet link in ExternalDescription (old admin AccessGUI "Data sheet"). */}
        {showAll && isAIM && (
          <div className={rowClass("ExternalDescription")}>
            <label title="Paste the full data sheet URL here.">Data sheet <StatusIcon kind="help" size={12} style={{ verticalAlign: "middle", color: "var(--color-text-muted)" }} /></label>
            <input className={styles.fieldInput} type="url" placeholder="https://..." value={product.ExternalDescription ?? ""}
              onChange={e => handleChange("ExternalDescription", e.target.value)} />
          </div>
        )}
        {isAccessOrAIM && (
          <div style={{ display: "flex", gap: "var(--space-3)" }}>
            {showAll && (
              <div className={rowClass("ExternalItemID")} style={{ maxWidth: 280 }}>
                <label title="Product ID from external system. Used for integrations and reconciliations between systems.">External Item ID <StatusIcon kind="help" size={12} style={{ verticalAlign: "middle", color: "var(--color-text-muted)" }} /></label>
                <input className={styles.fieldInput} value={product.ExternalItemID} onChange={e => handleChange("ExternalItemID", e.target.value)} />
              </div>
            )}
            <div className={rowClass("ExternalCost")} style={{ flex: 1, maxWidth: 150 }}>
              <label title="Cost of the product from external system. Used for ROI-calculations.">{req("External Cost")} <StatusIcon kind="help" size={12} style={{ verticalAlign: "middle", color: "var(--color-text-muted)" }} /></label>
              <input className={styles.fieldInput} type="number" step="0.01" min="0" value={product.ExternalCost}
                onChange={e => handleChange("ExternalCost", Math.max(0, parseFloat(e.target.value) || 0))} />
            </div>
            {showAll && (
              <>
                <div className={styles.fieldRowStacked} style={{ flex: 1, maxWidth: 150 }}>
                  <label title="Used for products that can vary in weight. A value of 10 means the product weight can vary 10%. This can be a value between 1 and 50. Leave it empty to use default value from kiosk setting.">
                    Weight Allowance <StatusIcon kind="help" size={12} style={{ verticalAlign: "middle", color: "var(--color-text-muted)" }} />
                  </label>
                  <input className={styles.fieldInput} type="number" step="1" min="1" max="50" value={product.WeightPercentAllowance ?? ""}
                    onChange={e => handleChange("WeightPercentAllowance", e.target.value === "" ? null : parseFloat(e.target.value))} />
                </div>
                <div className={styles.fieldRowStacked} style={{ flex: 1, maxWidth: 150 }}>
                  <label title="Minimum age required to purchase this product.">Age Restriction <StatusIcon kind="help" size={12} style={{ verticalAlign: "middle", color: "var(--color-text-muted)" }} /></label>
                  <input className={styles.fieldInput} type="number" step="1" value={product.AgeRestriction}
                    onChange={e => handleChange("AgeRestriction", parseInt(e.target.value) || 0)} />
                </div>
                {isAccess && (
                  <div className={styles.fieldRowStacked} style={{ flex: 1, maxWidth: 150, justifyContent: "center" }}>
                    <label>Is Expirable</label>
                    <input type="checkbox" style={{ alignSelf: "flex-start" }} checked={!!product.IsExpirable} onChange={e => handleChange("IsExpirable", e.target.checked)} />
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </AccordionSection>

      {/* ── Attributes ────────────────────── */}
      <AccordionSection title="Attributes" collapsed={collapsed["details"]} onToggle={() => toggle("details")}>
        {showAll && (
          <div style={{ display: "flex", gap: "var(--space-3)" }}>
            <div className={styles.fieldRowStacked} style={{ flex: 1 }}>
              <label>Brand</label>
              <input className={styles.fieldInput} value={product.Brand} onChange={e => handleChange("Brand", e.target.value)} />
            </div>
            <div className={styles.fieldRowStacked} style={{ flex: 1 }}>
              <label>{isOtherModule ? "Short Description" : "Article Number"}</label>
              <input className={styles.fieldInput} value={product.ArticleNumber} onChange={e => handleChange("ArticleNumber", e.target.value)} />
            </div>
            <div className={styles.fieldRowStacked} style={{ flex: 1 }}>

            </div>
          </div>
        )}
        <div style={{ display: "flex", gap: "var(--space-3)" }}>
          <div className={styles.fieldRowStacked} style={{ flex: 2 }}>
            <label>{req("Description")}</label>
            <FwRichTextarea
              value={product.Description}
              onChange={v => handleChange("Description", v)}
              rows={4}
            />
            <p className={styles.hint}>Wrap text in &lt;fw&gt;…&lt;/fw&gt; to use fixed-width font for that block (e.g. for aligned nutrition tables).</p>
          </div>
          {showAll && !isAIM && !isNew && (
            <div className={styles.fieldRowStacked} style={{ flex: 1 }}>
              <label>Localize Description</label>
              <p className={styles.hint}>Edit product name and description for other languages. The kiosk will use the localized text when that language is selected.</p>
              <div className={styles.fieldRowStacked} style={{ maxWidth: 400 }}>
                <label>Language</label>
                <select className={styles.fieldSelect} value={selectedLanguage} onChange={e => setSelectedLanguage(e.target.value)}>
                  <option value="">Select language...</option>
                  {languages.map(l => <option key={l.id} value={l.label}>{l.label}</option>)}
                </select>
              </div>
              <button
                className={styles.secondaryBtn}
                disabled={!selectedLanguage}
                onClick={() => setLocalizedOpen(true)}
                style={{ marginTop: "var(--space-1)", maxWidth: 150 }}
              >
                Localize Description
              </button>
              <LocalizedModal
                open={localizedOpen}
                onClose={() => setLocalizedOpen(false)}
                productId={product.ProductID}
                operatorId={operatorId}
                language={selectedLanguage}
              />
            </div>
          )}
          {showAll && isAIM && (
            <div className={styles.fieldRowStacked} style={{ flex: 1 }} />
          )}
        </div>
        {showAll && !isAccess && (
          <div className={styles.fieldRowStacked}>
            <label>Edit External Info</label>
            <p className={styles.hint}>The external fields are not used on all kiosk software. It depends on if you have requested additional features.</p>
            <div className={styles.inlineActions}>
              <button className={styles.secondaryBtn} onClick={() => {
                if (isNew) { setMessage({ type: "error", text: "You can only apply external info to an already created product." }); return; }
                setExternalInfoOpen(true);
              }} disabled={isNew}>
                Edit External Info
              </button>
            </div>
            {isNew && <p className={styles.hint} style={{ color: "var(--color-error)" }}>Create the product first.</p>}
          </div>
        )}

        {showAll && isAIM && (
          <div className={styles.fieldRowStacked} style={{ maxWidth: 280 }}>
            <label>Accounting Code</label>
            <input className={styles.fieldInput} value={product.ProductDepartment} onChange={e => handleChange("ProductDepartment", e.target.value)} />
          </div>
        )}

        {showAll && !isAccess && (
          <>
            <div style={{ display: "flex", gap: "var(--space-3)" }}>
              <div className={styles.fieldRowStacked} style={{ flex: 1 }}>
                <label>External Name</label>
                <input className={styles.fieldInput} value={product.ExternalName} onChange={e => handleChange("ExternalName", e.target.value)} />
              </div>
              <div className={styles.fieldRowStacked} style={{ flex: 1 }}>
                <label>External Article Nr</label>
                <input className={styles.fieldInput} value={product.ExternalArticleNr} onChange={e => handleChange("ExternalArticleNr", e.target.value)} />
              </div>
            </div>
            {!isAIM && (
              <div className={styles.fieldRowStacked}>
                <label>External Description</label>
                <FwRichTextarea
                  value={product.ExternalDescription}
                  onChange={v => handleChange("ExternalDescription", v)}
                  rows={3}
                />
              </div>
            )}
          </>
        )}

        {showAll && isAIM && (
          <div className={styles.fieldRowStacked}>
            <label>Video URL</label>
            <input className={styles.fieldInput} value={product.VideoUrl} onChange={e => handleChange("VideoUrl", e.target.value)} />
          </div>
        )}

        {/* ── Categories ── */}
        <h4 style={{ fontSize: "var(--text-sm)", fontWeight: "var(--font-semibold)" as never, marginTop: "var(--space-3)", marginBottom: "var(--space-2)" }}>{req("Categories")}</h4>
        <div className={styles.inlineActions}>
          <select className={styles.fieldSelect} value={categoryComboValue} onChange={e => setCategoryComboValue(e.target.value)} style={{ maxWidth: 300 }}>
            <option value="">Select category...</option>
            {availableCategories.map(c => (
              <option key={c.CategoryID} value={c.CategoryID}>{c.CategoryName} (OperatorID: {c.OperatorID ?? 0})</option>
            ))}
          </select>
          <button className={styles.secondaryBtn} onClick={() => {
            const catId = parseInt(categoryComboValue);
            if (!catId) return;
            if (assignedCategories.some(c => c.CategoryID === catId)) { setMessage({ type: "error", text: "Category already assigned." }); return; }
            const cat = availableCategories.find(c => c.CategoryID === catId);
            if (!cat) return;
            const newAssigned = [...assignedCategories, { CategoryID: catId, CategoryName: `${cat.CategoryName} (OperatorID: ${cat.OperatorID ?? 0})` }];
            setAssignedCategories(newAssigned);
            handleChange("CategoryIDs", newAssigned.map(c => c.CategoryID).join(","));
            setCategoryComboValue("");
          }}>Add Category</button>
        </div>
        {assignedCategories.length > 0 && (
          <ul className={styles.list} style={{ marginTop: "var(--space-2)", maxHeight: 120, overflowY: "auto", border: "1px solid var(--color-border-default)", borderRadius: "var(--radius-1)" }}>
            {assignedCategories.map((cat, idx) => (
              <li key={cat.CategoryID} className={styles.listItem} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span>{cat.CategoryName}</span>
                <button
                  className={styles.secondaryBtn}
                  style={{ padding: "2px 8px", fontSize: "var(--text-xs)" }}
                  onClick={() => {
                    const updated = assignedCategories.filter((_, i) => i !== idx);
                    setAssignedCategories(updated);
                    handleChange("CategoryIDs", updated.map(c => c.CategoryID).join(","));
                  }}
                >Delete</button>
              </li>
            ))}
          </ul>
        )}

        {/* ── Barcodes ── */}
        {showAll && (
          <>
            <h4 style={{ fontSize: "var(--text-sm)", fontWeight: "var(--font-semibold)" as never, marginTop: "var(--space-3)", marginBottom: "var(--space-2)" }}>Barcodes</h4>
            <div className={styles.inlineActions}>
              <input
                className={styles.fieldInput}
                value={barcodeInput}
                onChange={e => setBarcodeInput(e.target.value)}
                placeholder="Enter barcode..."
                style={{ maxWidth: 300 }}
              />
              <button className={styles.secondaryBtn} onClick={() => {
                const val = barcodeInput.trim();
                if (!val) return;
                const existing = product.BarcodeNr ? product.BarcodeNr.split(",").filter(Boolean) : [];
                if (existing.includes(val)) { setMessage({ type: "error", text: "Barcode already exists." }); return; }
                existing.push(val);
                handleChange("BarcodeNr", existing.join(","));
                setBarcodeInput("");
              }}>Add Barcode</button>
            </div>
            {product.BarcodeNr && product.BarcodeNr.split(",").filter(Boolean).length > 0 && (
              <ul className={styles.list} style={{ marginTop: "var(--space-2)", maxHeight: 120, overflowY: "auto", border: "1px solid var(--color-border-default)", borderRadius: "var(--radius-1)" }}>
                {product.BarcodeNr.split(",").filter(Boolean).map((bc, idx) => (
                  <li key={idx} className={styles.listItem} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontFamily: "var(--font-family-mono, monospace)", fontSize: "var(--text-sm)" }}>{bc}</span>
                    <button
                      className={styles.secondaryBtn}
                      style={{ padding: "2px 8px", fontSize: "var(--text-xs)" }}
                      onClick={() => {
                        const barcodes = product.BarcodeNr.split(",").filter(Boolean);
                        barcodes.splice(idx, 1);
                        handleChange("BarcodeNr", barcodes.join(","));
                      }}
                    >Delete</button>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </AccordionSection>

      {/* ── Relations ─────────────────────────────── */}
      {showAll && (
        <AccordionSection title="Product Relations" collapsed={collapsed["relations"]} onToggle={() => toggle("relations")}>
          <ProductRelationsSection productId={product.ProductID} isNew={isNew} onTempRelationsChange={setTempRelations} />
        </AccordionSection>
      )}

      {/* ── Images ─────────────────────────────────── */}
      {showAll && (
        <AccordionSection title="Images" collapsed={collapsed["image"]} onToggle={() => toggle("image")}>
        <div style={{ display: "flex", gap: "var(--space-4)" }}>
          {/* Product Image */}
          <div style={{ flex: 1 }}>
            
            <h4 style={{ fontSize: "var(--text-sm)", fontWeight: "var(--font-semibold)" as never, marginBottom: "var(--space-2)" }}>Product Image</h4>
            {!productCreated ? (
              <>
                <button type="button" className={styles.secondaryBtn} onClick={() => setChooseImageOpen(true)} disabled={saving}>
                  {pendingExistingImage ? "Choose Another Image" : "Choose Existing Image"}
                </button>
                <p className={styles.hint}>Upload a new image after the product is created.</p>
                {pendingExistingImage && (
                  <div style={{ marginTop: "var(--space-2)" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", flexWrap: "wrap" }}>
                      <span className={styles.hint}>{pendingExistingImage.ImageName}: set when the product is created.</span>
                      <button type="button" className={styles.secondaryBtn} onClick={() => setPendingExistingImage(null)} disabled={saving}>
                        Remove
                      </button>
                    </div>
                    {(() => {
                      const src = buildProductImageUrl(cache, pendingExistingImage.ImageName, pendingExistingImage.OperatorID);
                      const fb = buildProductImageUrlFallback(cache, pendingExistingImage.ImageName);
                      if (!src) return null;
                      return (
                        <div style={{ textAlign: "center", padding: "var(--space-2)" }}>
                          <ProductImage
                            src={src}
                            fallback={fb || undefined}
                            alt={pendingExistingImage.ImageName}
                            style={{ maxHeight: 200, maxWidth: 300, border: "1px solid var(--color-border-default)" }}
                          />
                        </div>
                      );
                    })()}
                  </div>
                )}
              </>
            ) : (
              <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap" }}>
                <FileInputSafe ref={imageInputRef} accept="image/*" style={{ display: "none" }}
                  onChange={e => { const f = e.target.files?.[0]; if (f) handleUploadProductImage(f); e.target.value = ""; }} />
                <button className={styles.secondaryBtn} onClick={() => imageInputRef.current?.click()} disabled={!!uploading}>
                  {uploading === "image" ? "Uploading..." : "Upload Image"}
                </button>
                <button type="button" className={styles.secondaryBtn} onClick={() => setChooseImageOpen(true)} disabled={!!uploading}>
                  Choose Existing Image
                </button>
              </div>
            )}
            {product.ImageUrl ? (
              <>
                <p className={styles.hint}>{product.ImageUrl}</p>
                {(() => {
                  const src = buildProductImageUrl(cache, product.ImageUrl, product.OperatorID);
                  const fb = buildProductImageUrlFallback(cache, product.ImageUrl);
                  if (!src) return (
                    <div style={{ textAlign: "center", padding: "var(--space-4) var(--space-2)", color: "var(--color-text-muted)", fontSize: "var(--text-sm)" }}>No image available</div>
                  );
                  return (
                    <div style={{ textAlign: "center", padding: "var(--space-2)" }}>
                      <ProductImage
                        src={src}
                        fallback={fb || undefined}
                        alt={product.Name}
                        style={{ maxHeight: 200, maxWidth: 300, border: "1px solid var(--color-border-default)" }}
                      />
                    </div>
                  );
                })()}
              </>
            ) : !isNew ? (
              <div style={{
                textAlign: "center",
                padding: "var(--space-4) var(--space-2)",
                marginTop: "var(--space-2)",
                border: "1px dashed var(--color-border-default)",
                borderRadius: "var(--radius-1)",
                color: "var(--color-text-muted)",
                fontSize: "var(--text-sm)",
              }}>
                No image
              </div>
            ) : null}
            
          </div>

          {/* Additional Images */}
          {isAccessOrAIM && (
            <div style={{ flex: 1 }}>
              <h4 style={{ fontSize: "var(--text-sm)", fontWeight: "var(--font-semibold)" as never, marginBottom: "var(--space-2)" }}>Additional Images</h4>
              {product.AdditionalImages && (
                <div style={{ fontSize: "var(--text-xs)", color: "var(--color-text-secondary)", marginBottom: "var(--space-2)" }}>
                  {product.AdditionalImages}
                </div>
              )}
              {isNew ? (
                <p className={styles.hint} style={{ color: "var(--color-error)" }}>Create the product first.</p>
              ) : (
                <>
                  <FileInputSafe ref={additionalImageInputRef} accept="image/*" style={{ display: "none" }}
                    onChange={e => { const f = e.target.files?.[0]; if (f) handleUploadAdditionalImage(f); e.target.value = ""; }} />
                  <button className={styles.secondaryBtn} onClick={() => additionalImageInputRef.current?.click()} disabled={!!uploading}>
                    {uploading === "additional" ? "Uploading..." : "Upload Additional Image"}
                  </button>
                </>
              )}
            </div>
          )}
        </div>
        <p className={styles.hint}>Max {IMAGE_MAX_SIZE_MB} MB, max {IMAGE_MAX_RESOLUTION_MP} MP. Accepted: png, jpg, gif, bmp.</p>
        </AccordionSection>
      )}

      {/* ── Price ─────────────────────────────────── */}
      <AccordionSection title={sectionTitle("Price", "Price")} collapsed={collapsed["price"]} onToggle={() => toggle("price")}>
        <ProductPriceEntry
          value={priceRec}
          onChange={(next) => {
            setPriceRec(next);
            // Only flag dirty on a real change. The panel emits once on mount with the
            // loaded values, but normalizes them (null→0, rounded to 4 dp), so compare on
            // that same basis — otherwise loading a product with an unset tax or a >4dp
            // stored value would trip the unsaved-changes guard with no edit made.
            const norm = (v: number | null | undefined) => Math.round((v ?? 0) * 1e4) / 1e4;
            if ([1, 2, 3, 4, 5, 6, 7, 8, 9].some((k) => norm(priceRec[k]) !== norm(next[k]))) setDirty(true);
          }}
          flags={flags}
          productType={productTypeCombo}
          showRental={showRental}
          showReserve={showReserve}
          showEatHere={showEatHere}
          hideReserveLateTaxrate={hideReserveLateTaxrate}
        />
      </AccordionSection>

      {/* ── Reserve Info ──────────────────────────── */}
      {showAll && showReserve && (
        <AccordionSection title="Reserve Info" collapsed={collapsed["reserve"]} onToggle={() => toggle("reserve")}>
          <div style={{ display: "flex", gap: "var(--space-3)" }}>
            <div className={styles.fieldRowStacked} style={{ flex: 1, maxWidth: 150 }}>
              <label>Rental Period (minutes)</label>
              <input className={styles.fieldInput} type="number" min="0" step="1"
                value={reserveExtra.RentalPeriodMinutes ?? ""}
                onChange={e => updateReserveExtra("RentalPeriodMinutes", e.target.value === "" ? undefined : parseInt(e.target.value))} />
            </div>
            <div className={styles.fieldRowStacked} style={{ flex: 1, maxWidth: 150 }}>
              <label>Max Rental Periods</label>
              <input className={styles.fieldInput} type="number" min="0" step="1"
                value={reserveExtra.MaxRentalPeriods ?? ""}
                onChange={e => updateReserveExtra("MaxRentalPeriods", e.target.value === "" ? undefined : parseInt(e.target.value))} />
            </div>
          </div>
          {Array.isArray(reserveExtra.IncludedItemInfo) && reserveExtra.IncludedItemInfo.length > 0 && (
            <div className={styles.fieldRowStacked} style={{ marginTop: "var(--space-2)" }}>
              <label>Included Item Info</label>
              <table className={styles.dataTable}>
                <thead>
                  <tr><th>Quantity</th><th>Products</th><th>Lists</th></tr>
                </thead>
                <tbody>
                  {reserveExtra.IncludedItemInfo.map((item, idx) => (
                    <tr key={idx}>
                      <td>{item.Quantity ?? ""}</td>
                      <td>{Array.isArray(item.Products) ? item.Products.join(", ") : ""}</td>
                      <td>{Array.isArray(item.Lists) ? item.Lists.join(", ") : ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </AccordionSection>
      )}

      {/* ── Post-create image upload modal ─────────── */}
      <Modal open={postCreateImageOpen} onClose={() => setPostCreateImageOpen(false)}>
        <ModalHeader title="Add Product Image" onClose={() => setPostCreateImageOpen(false)} />
        <ModalBody>
          <p style={{ marginBottom: "var(--space-2)" }}>Product created successfully. Would you like to upload a product image now?</p>
          <FileInputSafe ref={postCreateImageRef} accept="image/*" style={{ display: "none" }}
            onChange={async e => {
              const f = e.target.files?.[0];
              if (f) {
                await handleUploadProductImage(f);
                setPostCreateImageOpen(false);
              }
              e.target.value = "";
            }} />
          {product.ImageUrl && (() => {
            const src = buildProductImageUrl(cache, product.ImageUrl, product.OperatorID);
            const fb = buildProductImageUrlFallback(cache, product.ImageUrl);
            if (!src) return (
              <div style={{ textAlign: "center", padding: "var(--space-4) var(--space-2)", marginBottom: "var(--space-2)", color: "var(--color-text-muted)", fontSize: "var(--text-sm)" }}>No image available</div>
            );
            return (
              <div style={{ textAlign: "center", padding: "var(--space-2)", marginBottom: "var(--space-2)" }}>
                <ProductImage
                  src={src}
                  fallback={fb || undefined}
                  alt={product.Name}
                  style={{ maxHeight: 200, maxWidth: 300, border: "1px solid var(--color-border-default)" }}
                />
              </div>
            );
          })()}
          <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap" }}>
            <button className={styles.primaryBtn} onClick={() => postCreateImageRef.current?.click()} disabled={!!uploading}>
              {uploading === "image" ? "Uploading..." : "Upload Image"}
            </button>
            <button type="button" className={styles.secondaryBtn} disabled={!!uploading}
              onClick={() => { setPostCreateImageOpen(false); setChooseImageOpen(true); }}>
              Choose Existing Image
            </button>
          </div>
          <p className={styles.hint} style={{ marginTop: "var(--space-1)" }}>Max {IMAGE_MAX_SIZE_MB} MB, max {IMAGE_MAX_RESOLUTION_MP} MP. Accepted: png, jpg, gif, bmp.</p>
        </ModalBody>
        <ModalFooter>
          <button className={styles.secondaryBtn} onClick={() => setPostCreateImageOpen(false)}>Skip</button>
        </ModalFooter>
      </Modal>

      {chooseImageOpen && (
        <ChooseProductImageModal
          operatorId={imageFolderOperatorId}
          currentImageName={productCreated ? product.ImageUrl : pendingExistingImage?.ImageName}
          chooseLabel={productCreated ? "Use image" : "Use when created"}
          onClose={() => setChooseImageOpen(false)}
          onChoose={handleChooseExistingImage}
        />
      )}

      {pendingImage && (() => {
        const isMain = pendingImage.kind === "main";
        const currentSrc = isMain ? buildProductImageUrl(cache, product.ImageUrl, product.OperatorID) : "";
        const currentAlt = isMain ? buildProductImageUrlFallback(cache, product.ImageUrl) : "";
        const title = isMain ? "Confirm product image" : "Confirm additional image";
        const instruction = isMain
          ? "Compare the current image with the validated upload. Confirm to replace; cancel to discard the upload."
          : "Preview of the validated upload. Confirm to add; cancel to discard.";
        return (
          <Modal open={true} onClose={cancelPendingImage}>
            <ModalHeader title={title} onClose={cancelPendingImage} />
            <ModalBody>
              <div style={{ display: "flex", gap: "var(--space-3)", justifyContent: "center", flexWrap: "wrap" }}>
                {isMain && (currentSrc ? (
                  <div style={{ textAlign: "center" }}>
                    <div className={styles.hint} style={{ marginBottom: "var(--space-1)" }}>Current</div>
                    <ProductImage
                      src={currentSrc}
                      fallback={currentAlt || undefined}
                      alt={product.Name}
                      style={{ maxHeight: 260, maxWidth: 300, border: "1px solid var(--color-border-default)" }}
                    />
                    <div className={styles.hint} style={{ marginTop: "var(--space-1)" }}>{product.ImageUrl}</div>
                  </div>
                ) : (
                  <div style={{
                    textAlign: "center",
                    minWidth: 200,
                    padding: "var(--space-4) var(--space-2)",
                    border: "1px dashed var(--color-border-default)",
                    borderRadius: "var(--radius-1)",
                    color: "var(--color-text-muted)",
                    fontSize: "var(--text-sm)",
                    alignSelf: "center",
                  }}>
                    No current image
                  </div>
                ))}
                <div style={{ textAlign: "center" }}>
                  <div className={styles.hint} style={{ marginBottom: "var(--space-1)" }}>New upload</div>
                  <img
                    src={pendingImage.previewUrl}
                    alt={pendingImage.imageName}
                    style={{ maxHeight: 260, maxWidth: 300, border: "1px solid var(--color-border-default)" }}
                  />
                  <div className={styles.hint} style={{ marginTop: "var(--space-1)" }}>{pendingImage.imageName}</div>
                </div>
              </div>
              <p className={styles.hint} style={{ marginTop: "var(--space-3)", textAlign: "center" }}>
                {instruction}
              </p>
            </ModalBody>
            <ModalFooter>
              <button className={styles.secondaryBtn} onClick={cancelPendingImage} disabled={confirming}>Cancel</button>
              <button className={styles.primaryBtn} onClick={confirmPendingImage} disabled={confirming}>
                {confirming ? "Saving..." : "Confirm & Save"}
              </button>
            </ModalFooter>
          </Modal>
        );
      })()}
    </>
  );
},
);

/* ── AccordionSection ──────────────────────────────────── */

function AccordionSection({ title, collapsed, onToggle, children, headerRight }: {
  /** Usually a plain string; accepts a node so a section can carry a change marker. */
  title: React.ReactNode;
  collapsed?: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  headerRight?: React.ReactNode;
}) {
  return (
    <SimpleFieldset legend="">
      <div style={{ display: "flex", alignItems: "center" }}>
        <button
          type="button"
          onClick={onToggle}
          style={{
            display: "flex", alignItems: "center", gap: "var(--space-1)",
            flex: 1, background: "none", border: "none", cursor: "pointer",
            fontSize: "var(--text-sm)", fontWeight: "var(--font-semibold)" as never,
            color: "var(--color-text)", padding: "var(--space-1) 0",
          }}
        >
          <span style={{ transform: collapsed ? "rotate(-90deg)" : "rotate(0deg)", transition: "transform 150ms", display: "inline-block" }}>▾</span>
          {title}
        </button>
        {headerRight}
      </div>
      {!collapsed && <div style={{ paddingTop: "var(--space-2)" }}>{children}</div>}
    </SimpleFieldset>
  );
}

/* ── Helpers ───────────────────────────────────────────── */

function emptyProduct(): AccessProduct {
  return {
    ProductID: 0, OperatorID: 0, Name: "", PluralName: "", ShortName: "", Brand: "",
    ArticleNumber: "", Description: "", ProductType: 0, BarcodeNr: "",
    PermissionID: "", PermissionName: "",
    BuyPrice: 0, BuyTax: 0, ExternalCost: 0,
    RentalPrice: 0, RentalTax: 0, EatHerePrice: 0, EatHereTax: 0,
    ReservePeriodPrice: null, ReserveLatePeriodPrice: null, ReserveLateTaxrate: null,
    UnitsPerPackage: 1, OrderMultiplier: 1,
    InsaneAmount: 0, AgeRestriction: 0, WeightPercentAllowance: null,
    PriceType: 0, ExternalItemID: "", ProductDepartment: "",
    ProductClass: "", ProductSubClass: "",
    ExternalDescription: "", ExternalName: "", ExternalArticleNr: "",
    ForceScan: false, IsExpirable: false, Deleted: false,
    VideoUrl: "", ImageUrl: "", AdditionalImages: "",
    Localization: "", ExternalInfo: "", ExtraInfo: "", LockHours: "",
    CategoryIDs: "", StoreLocationIDs: "", SlotColor: "",
    PdfFiles: "", ReserveInfo: "", IncludedItems: "",
  };
}

/** Self-contained image with primary→fallback→placeholder lifecycle in React state.
 *  Avoids DOM-level src swaps that fight with React reconciliation. */
function ProductImage({ src, fallback, alt, style }: {
  src: string; fallback?: string; alt: string;
  style?: React.CSSProperties;
}) {
  const [phase, setPhase] = useState<"primary" | "fallback" | "failed">("primary");
  // Reset phase when src changes (e.g. after uploading a new image)
  const prevSrc = useRef(src);
  if (prevSrc.current !== src) { prevSrc.current = src; if (phase !== "primary") setPhase("primary"); }

  if (phase === "failed") {
    return (
      <div style={{ textAlign: "center", padding: "var(--space-4) var(--space-2)", color: "var(--color-text-muted)", fontSize: "var(--text-sm)" }}>
        No image available
      </div>
    );
  }
  const activeSrc = phase === "fallback" && fallback ? fallback : src;
  return (
    <img
      src={activeSrc}
      alt={alt}
      onError={() => {
        if (phase === "primary" && fallback && fallback !== src) {
          setPhase("fallback");
        } else {
          setPhase("failed");
        }
      }}
      style={style}
    />
  );
}
